import { Injectable, signal } from '@angular/core';

/** All supported chat sidebar types, including the geotechnical analysis chat. */
export type ChatSidebarType = 'document' | 'qna' | 'compare' | 'geotechnical';

export interface ChatSidebarConfig {
    contractId: string;
    documentTypeId?: string;
    projectId?: string;
    comparisonId?: string;
    compareDocuments?: { _id: string; name: string }[];
    kpiDocuments?: { _id: string; name: string }[];
    formQuestions?: { _id: string; name: string; sectionTitle?: string }[];
    selectedFormQuestionId?: string | null;
    embeddingsStatus?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChatSidebarService {
    readonly isOpen = signal<boolean>(false);
    readonly chatType = signal<ChatSidebarType | null>(null);
    readonly chatConfig = signal<ChatSidebarConfig>({ contractId: '' });

    /**
     * Type of the floating bubble currently registered by an active page.
     * null when no page has registered a bubble (hides the bubble globally).
     */
    readonly bubbleType = signal<ChatSidebarType | null>(null);

    /**
     * Config for the floating bubble. Populated by the page that owns the bubble context.
     */
    readonly bubbleConfig = signal<ChatSidebarConfig | null>(null);

    open(type: ChatSidebarType, config: ChatSidebarConfig): void {
        this.chatType.set(type);
        this.chatConfig.set(config);
        this.isOpen.set(true);
    }

    close(): void {
        this.isOpen.set(false);
    }

    updateConfig(partial: Partial<ChatSidebarConfig>): void {
        this.chatConfig.update(current => ({ ...current, ...partial }));
    }

    /**
     * Called by a page component (e.g. GeoTechnicalAnalysisReportComponent) on init
     * to register context for the global floating bubble.
     */
    registerBubble(type: ChatSidebarType, config: ChatSidebarConfig): void {
        this.bubbleType.set(type);
        this.bubbleConfig.set(config);
    }

    /**
     * Called by a page component on destroy to hide the global bubble and
     * close the sidebar if it was opened via this bubble.
     */
    clearBubble(): void {
        this.bubbleType.set(null);
        this.bubbleConfig.set(null);
        if (this.isOpen()) {
            this.close();
        }
    }
}
